import { Component, computed, inject } from '@angular/core';
import { AnalysisConfigurationResponse } from '@api-response-types/analysis-configuration.response';
import { AnalysisCalculationLevel } from '@api-types/enums/analysis-calculation-level.enum';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { AnalysisVersionStatus } from '@api-types/enums/analysis-version-status.enum';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { TwoDecimalPercentPipe } from '@core/utils/two-decimal-percent.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnalysisContextService } from 'demand-forecasting/analysis/services/analysis-context.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { PmKpiCardComponent } from '@libs/ui';

const CALCULATION_LEVEL_KPI_KEYS: Record<
  AnalysisCalculationLevel,
  { labelKey: string; tooltipKey: string }
> = {
  [AnalysisCalculationLevel.LOWEST]: {
    labelKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST_CHIP',
    tooltipKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST_DESCRIPTION',
  },
  [AnalysisCalculationLevel.HIGHEST]: {
    labelKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST_CHIP',
    tooltipKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST_DESCRIPTION',
  },
};

@Component({
  selector: 'pm-analysis-version-metadata',
  templateUrl: './analysis-version-metadata.component.html',
  providers: [TwoDecimalPercentPipe],
  imports: [TranslateModule, PmKpiCardComponent],
})
export class AnalysisVersionMetadataComponent {
  protected AnalysisVersionStatus = AnalysisVersionStatus;
  protected AnalysisType = AnalysisType;
  protected analysisStateService = inject(AnalysisStateService);
  private readonly analysisContextService = inject(AnalysisContextService);
  protected readonly dateUtilsService = inject(DateUtilsService);
  private readonly translate = inject(TranslateService);

  protected readonly timePeriodValue =
    this.analysisContextService.timePeriodValue;

  protected cycleKpiValue(
    configuration: AnalysisConfigurationResponse,
  ): string {
    const name = configuration.anchorConsensusCycleName;
    if (!name) {
      return '/';
    }
    return this.translate.instant('ANALYSIS.CYCLE_VALUE', {
      name,
      date: this.dateUtilsService.formatDateBasedOnLanguage(
        configuration.anchorConsensusCycleFinishedAt ?? undefined,
      ),
    });
  }

  protected readonly calculationLevelKpi = computed(() => {
    const calculationLevel =
      this.analysisStateService.selectedVersion()?.calculationLevel;
    if (!calculationLevel) {
      return undefined;
    }

    return CALCULATION_LEVEL_KPI_KEYS[calculationLevel];
  });

  protected readonly timePeriodTooltip = computed(
    (): { key: string; horizon: string } | undefined => {
      const version = this.analysisStateService.selectedVersion();
      const horizon = this.analysisContextService.horizon();
      if (!version || !horizon) {
        return undefined;
      }

      const hasCycle = version.anchorConsensusCycleId !== null;
      const hasForecastVersion = version.anchorForecastVersionId !== null;

      if (version.analysisType !== AnalysisType.SINGLE_PERIOD) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_ROLLING', horizon };
      }
      if (hasCycle && hasForecastVersion) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_SINGLE', horizon };
      }
      if (hasCycle) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_CYCLE', horizon };
      }
      return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_VERSION', horizon };
    },
  );
}
